/**
 * Read-only request view (canvas OnboardingScreen / OffboardingScreen in view mode). An offboarding row shows the
 * leaver, the termination instant in Central (with the ISO UTC next to it), the delegates, the equipment, and the
 * leaver's Intune devices as a read-only panel (Phase 2c). The server decides `cancel.canCancel` / `pastWindow` and
 * the automation state; this view never re-derives the 72-hour rule. Cancel keeps the row (cancelled_at /
 * cancelled_by) where the canvas deleted it; the list stops showing it either way.
 *
 * Phase 3a: the Automation pill reads the five server states; a holding request shows "Holding until <deadline>"
 * and, for a Super Admin, a Force start button behind a confirm (the canvas could only tick the box at submit).
 * While the state is Holding the view re-fetches every 30 s so the page follows the run; after a force start or a
 * cancel it re-fetches at once and stays on the page.
 */
import { html, nothing, type TemplateResult } from 'lit-html';
import { ApiError, cancelRequest, forceStartRequest, getRequest, retrySalesforce, type RequestDetail, type SalesforceOutcome } from './api';
import type { App } from './app';
import { clearDevices, devicesPanel, lookupDevices, newDevices } from './devices';
import { day, instant, statusPill } from './format';

const POLL_MS = 30_000;

const st = {
  id: 0,
  data: null as RequestDetail | null,
  loading: false,
  error: '',
  notFound: false,
  confirm: false,
  cancelling: false,
  cancelError: '',
  /** Force start (Super Admin, holding request): confirm open, in flight, last error. */
  forceConfirm: false,
  forcing: false,
  forceError: '',
  /** Phase 3c Retry Salesforce provisioning (IT / Super Admin): confirm open, in flight, last error. */
  retryConfirm: false,
  retrying: false,
  retryError: '',
  /** The 30-second refresh while the request is holding; 0 when none is scheduled. */
  poll: 0,
  /** Offboarding only: the leaver's Intune devices, looked up when the view opens (reporting for IT, nothing stored). */
  devices: newDevices(),
};

function stopPoll(): void {
  if (st.poll) window.clearTimeout(st.poll);
  st.poll = 0;
}

export function enterDetail(app: App, id: number): void {
  clearDevices(st.devices);
  stopPoll();
  Object.assign(st, {
    id,
    data: null,
    loading: true,
    error: '',
    notFound: false,
    confirm: false,
    cancelling: false,
    cancelError: '',
    forceConfirm: false,
    forcing: false,
    forceError: '',
    retryConfirm: false,
    retrying: false,
    retryError: '',
  });
  void load(app);
}

/** True while the user is still looking at this request (the poll must not redraw another route). */
const onThisPage = (id: number) => location.hash === `#/requests/${id}`;

async function load(app: App, quiet = false): Promise<void> {
  const id = st.id;
  stopPoll();
  try {
    const d = await getRequest(id);
    if (id !== st.id) return;
    st.data = d;
    if (d.offboarding && !quiet) {
      lookupDevices(app, st.devices, d.offboarding.employeeEntraId || d.offboarding.employeeEmail, d.offboarding.employeeName || d.request.displayName, 0);
    }
    if (d.automation.state === 'Holding' && onThisPage(id)) {
      st.poll = window.setTimeout(() => {
        if (st.id === id && onThisPage(id)) void load(app, true);
      }, POLL_MS);
    }
  } catch (e) {
    if (id !== st.id) return;
    if (e instanceof ApiError && e.status === 404) st.notFound = true;
    else st.error = (e as Error).message || 'The request could not be loaded.';
  }
  st.loading = false;
  app.redraw();
}

async function doCancel(app: App): Promise<void> {
  st.cancelling = true;
  st.cancelError = '';
  app.redraw();
  try {
    await cancelRequest(st.id);
    app.notify(`Request cancelled: ${st.data?.request.displayName ?? ''}`);
    st.confirm = false;
    st.cancelling = false;
    await load(app, true);
  } catch (e) {
    st.cancelling = false;
    st.cancelError = (e as Error).message || 'The request could not be cancelled.';
    app.redraw();
  }
}

async function doForceStart(app: App): Promise<void> {
  st.forcing = true;
  st.forceError = '';
  app.redraw();
  try {
    await forceStartRequest(st.id);
    app.notify('Hold skipped. Provisioning starts within a minute.');
    st.forceConfirm = false;
    st.forcing = false;
    await load(app, true);
  } catch (e) {
    st.forcing = false;
    st.forceError = (e as Error).message || 'The hold could not be skipped.';
    app.redraw();
  }
}

async function doRetrySalesforce(app: App): Promise<void> {
  st.retrying = true;
  st.retryError = '';
  app.redraw();
  try {
    await retrySalesforce(st.id);
    app.notify('Salesforce provisioning started again. This page refreshes in a moment.');
    st.retryConfirm = false;
    st.retrying = false;
    // The retry run takes a few seconds; re-read once now and once more shortly after.
    await load(app, true);
    window.setTimeout(() => {
      if (onThisPage(st.id)) void load(app, true);
    }, 15_000);
  } catch (e) {
    st.retrying = false;
    st.retryError = e instanceof ApiError && e.status === 409 ? `${e.message}.` : (e as Error).message || 'Salesforce provisioning could not be started.';
    app.redraw();
  }
}

/** Phase 3c default 17: one line per outcome, from the run output (never from error rows). */
export function salesforceLine(o: SalesforceOutcome | null | undefined): string | null {
  if (!o) return null;
  switch (o.kind) {
    case 'created':
      return `Salesforce user created (${o.username ?? '?'})`;
    case 'exists':
      return `Salesforce: already existed (${o.username ?? '?'})`;
    case 'contact-only':
      return o.contactAction === 'multiple' ? 'Salesforce: several Contacts match, set Active Employee by hand' : 'Salesforce: Contact marked Active Employee';
    case 'blocked':
      return o.licence ? `Salesforce: blocked, no free licence (${o.licence.used} of ${o.licence.total})` : 'Salesforce: blocked, no free licence';
    case 'mirror-missing':
      return `Salesforce: mirror ${o.mirror?.email ?? o.reason ?? '?'} not found`;
    case 'failed':
      return `Salesforce: provisioning failed${o.failedStep ? ` (${o.failedStep})` : ''}`;
    case 'skipped':
      return o.reason === 'termination-failed' ? 'Salesforce: skipped (termination failed)' : o.reason === 'not-requested' ? null : `Salesforce: skipped (${o.reason ?? ''})`;
    case 'deactivated':
      return `Salesforce user deactivated, ${o.routesEndDated ?? 0} route assignment${o.routesEndDated === 1 ? '' : 's'} ended`;
    case 'user-not-found':
      return `Salesforce: no user for this account, ${o.routesEndDated ?? 0} route assignment${o.routesEndDated === 1 ? '' : 's'} ended`;
    case 'deactivation-failed':
      return `Salesforce: offboarding failed${o.failedStep ? ` (${o.failedStep})` : ''}`;
    default:
      return null;
  }
}

/** The second Salesforce line for an onboarding: Geopointe and permission sets, when there is something to say. */
function salesforceExtras(o: SalesforceOutcome | null | undefined): string | null {
  if (!o) return null;
  const parts: string[] = [];
  if (o.geopointe === 'assigned') parts.push('Geopointe licence assigned');
  else if (o.geopointe === 'already') parts.push('Geopointe licence already held');
  else if (o.geopointe === 'blocked') parts.push('Geopointe licences full');
  if (o.permissionSetsFailed?.length) parts.push(`${o.permissionSetsFailed.length} permission set(s) not assigned`);
  return parts.length ? parts.join(' · ') : null;
}

const yesNo = (b: boolean | null | undefined) => (b ? 'Yes' : 'No');

function dl(rows: [string, unknown][]): TemplateResult {
  return html`<dl class="facts">
    ${rows.map(
      ([k, v]) => html`<div><dt>${k}</dt><dd>${v === null || v === undefined || v === '' ? html`<span class="none">—</span>` : v}</dd></div>`,
    )}
  </dl>`;
}

function items(title: string, xs: TemplateResult[] | string[]): TemplateResult {
  return html`<div class="facet"><h4>${title}</h4>${xs.length
    ? html`<ul class="tidy">${xs.map((x) => html`<li>${x}</li>`)}</ul>`
    : html`<p class="none">None</p>`}</div>`;
}

function confirmDialog(app: App, d: RequestDetail): TemplateResult {
  if (!st.confirm) return html``;
  // Canvas OB_DeleteConfirmLabel (L3914-3917), worded for cancel.
  const message = d.cancel.pastWindow
    ? 'This request is past the 72-hour window, so provisioning already ran. Cancelling hides the request but does NOT undo accounts, licenses, or tasks. Continue?'
    : 'Cancel this request? Nothing has been provisioned yet. Cancelling withdraws it, and you can submit a new request.';
  return html`
    <div class="modal-scrim">
      <div class="modal card" role="dialog" aria-modal="true" aria-labelledby="cx-title">
        <h2 id="cx-title">${d.cancel.pastWindow ? 'Past the 72-hour window' : 'Cancel this request?'}</h2>
        <p>${message}</p>
        ${st.cancelError ? html`<p class="ferr" role="alert">${st.cancelError}</p>` : nothing}
        <div class="modal-acts">
          <button type="button" class="btn danger" ?disabled=${st.cancelling} @click=${() => doCancel(app)}>
            ${st.cancelling ? 'Cancelling…' : 'Cancel request'}
          </button>
          <button type="button" class="btn ghost" ?disabled=${st.cancelling} @click=${() => {
            st.confirm = false;
            app.redraw();
          }}>Keep it</button>
        </div>
      </div>
    </div>`;
}

/** Phase 3c default 13: "Provision <name> in Salesforce now?" for IT and Super Admin. */
function retryDialog(app: App): TemplateResult {
  if (!st.retryConfirm) return html``;
  const name = st.data?.request.displayName ?? 'this hire';
  return html`
    <div class="modal-scrim">
      <div class="modal card" role="dialog" aria-modal="true" aria-labelledby="sfr-title">
        <h2 id="sfr-title">Retry Salesforce provisioning</h2>
        <p>Provision ${name} in Salesforce now? The run finds the mirror user again, checks for a free licence and creates the user, permission sets, Geopointe licence and Contact link that are still missing. Nothing is created twice.</p>
        ${st.retryError ? html`<p class="ferr" role="alert">${st.retryError}</p>` : nothing}
        <div class="modal-acts">
          <button type="button" class="btn" ?disabled=${st.retrying} @click=${() => doRetrySalesforce(app)}>
            ${st.retrying ? 'Starting…' : 'Provision now'}
          </button>
          <button type="button" class="btn ghost" ?disabled=${st.retrying} @click=${() => {
            st.retryConfirm = false;
            app.redraw();
          }}>Not now</button>
        </div>
      </div>
    </div>`;
}

function forceStartDialog(app: App): TemplateResult {
  if (!st.forceConfirm) return html``;
  return html`
    <div class="modal-scrim">
      <div class="modal card" role="dialog" aria-modal="true" aria-labelledby="fs-title">
        <h2 id="fs-title">Force start</h2>
        <p>Skip the rest of the 72-hour hold and start now?</p>
        ${st.forceError ? html`<p class="ferr" role="alert">${st.forceError}</p>` : nothing}
        <div class="modal-acts">
          <button type="button" class="btn" ?disabled=${st.forcing} @click=${() => doForceStart(app)}>
            ${st.forcing ? 'Starting…' : 'Start now'}
          </button>
          <button type="button" class="btn ghost" ?disabled=${st.forcing} @click=${() => {
            st.forceConfirm = false;
            app.redraw();
          }}>Keep holding</button>
        </div>
      </div>
    </div>`;
}

export function detailView(app: App): TemplateResult {
  const back = html`<p><a class="back" href="#/">← Requests</a></p>`;
  if (st.loading) return html`<main id="main" class="wrap">${back}<div class="card"><p>Loading…</p></div></main>`;
  if (st.notFound) {
    return html`<main id="main" class="wrap">${back}<div class="card"><h3>Not found</h3><p>There is no request #${st.id} that you can see.</p></div></main>`;
  }
  if (st.error || !st.data) {
    return html`<main id="main" class="wrap">${back}<div class="notice err" role="alert">${st.error}
      <button class="mini" type="button" @click=${() => enterDetail(app, st.id)}>Try again</button></div></main>`;
  }
  const d = st.data;
  const r = d.request;
  const on = d.onboarding;
  const off = d.offboarding;
  const holding = d.automation.state === 'Holding' && !r.cancelledAt;
  const canForceStart = holding && app.me.access.canForceStart;
  // Phase 3c default 13: IT and Super Admin may retry a blocked / mirror-missing / failed Salesforce provisioning.
  const canRetrySalesforce = !!d.salesforce?.canRetry && !r.cancelledAt && (app.me.access.roles.includes('IT') || app.me.access.isSuperAdmin);
  const sfLine = salesforceLine(d.salesforce?.outcome);
  const sfExtras = salesforceExtras(d.salesforce?.outcome);
  const sfBusy = !!d.salesforce?.busy && d.salesforce?.retry.runtimeStatus != null && d.salesforce.from !== 'retry';

  return html`
    <main id="main" class="wrap detail">
      ${back}
      <div class="hello">
        <div>
          <h1>${r.displayName}</h1>
          <div class="meta">
            <span class="tag">${r.requestType}</span><span class="tag">${r.personType}</span><span>${r.department}</span>
            <span class="muted">Request #${r.id}</span>
          </div>
          <p class="muted">Submitted ${instant(r.createdAt)} by ${r.createdByName ?? r.createdByEmail}${r.forceStart ? ' · 72-hour hold skipped' : ''}</p>
        </div>
        <div class="acts">
          ${canForceStart
            ? html`<button type="button" class="btn ghost" @click=${() => {
                st.forceError = '';
                st.forceConfirm = true;
                app.redraw();
              }}>Force start</button>`
            : nothing}
          ${canRetrySalesforce
            ? html`<button type="button" class="btn ghost" @click=${() => {
                st.retryError = '';
                st.retryConfirm = true;
                app.redraw();
              }}>Retry Salesforce provisioning</button>`
            : nothing}
          ${d.cancel.canCancel
            ? html`<button type="button" class="btn ghost danger-text" @click=${() => {
                st.confirm = true;
                app.redraw();
              }}>Cancel request</button>`
            : nothing}
        </div>
      </div>
      ${retryDialog(app)}

      ${r.cancelledAt
        ? html`<div class="notice">This request was cancelled ${instant(r.cancelledAt)} by ${r.cancelledBy}.</div>`
        : d.cancel.canCancel && !d.cancel.pastWindow
          ? html`<p class="muted">Can be cancelled until ${instant(d.cancel.cancelWindowEndsAt)} (72 hours after it was submitted).</p>`
          : nothing}

      <section class="card">
        <h3>Request status</h3>
        <div class="statuses">
          ${d.statuses.map((x) => html`<div class="status ${x.scope === 'Overall' ? 'overall' : ''}"><span>${x.scope}</span>${statusPill(x.status)}</div>`)}
          <div class="status automation"><span>Automation</span>${statusPill(d.automation.state)}</div>
        </div>
        <p class="hint">Only departments with tasks on this request are shown.</p>
        ${holding && d.automation.holdEndsAt
          ? html`<p class="muted">Holding until ${instant(d.automation.holdEndsAt)}. Provisioning starts then${canForceStart ? ', or now with Force start' : ''}.</p>`
          : nothing}
        ${d.automation.state === 'With issues'
          ? html`<p class="muted">Automation recorded ${d.automation.openErrors} unacknowledged error(s) on this request. Anything not done automatically needs a person.</p>`
          : nothing}
      </section>

      ${on
        ? html`<section class="card">
            <h3>New hire</h3>
            ${dl([
              ['First name', on.firstName],
              ['Last name', on.lastName],
              ['Preferred name', on.preferredName],
              ['New or transfer', on.newOrTransfer],
              ['Phone number', on.phoneNumber],
              ['Office', on.officeLocation],
              ['Job title', on.jobTitle],
              ['Department', r.department],
              ['Manager', r.managerEmail],
              ['Start day', day(on.startDay)],
              ['Has direct reports', yesNo(on.hasDirectReports)],
              ['Role template', on.roleTemplateTitle],
            ])}
          </section>
          <section class="card">
            <h3>Access</h3>
            ${dl([
              ['Salesforce access', yesNo(on.salesforceAccess)],
              ['Salesforce mirror user', on.salesforceAccess ? on.salesforceMirrorEmail : null],
              ['Teams phone', yesNo(on.teamsPhone)],
              ['Teams extension requested', on.teamsPhone ? (on.teamsExtensionRequested ?? 'Next free number') : null],
              ['Teams extension assigned', on.teamsExtensionAssigned],
              ['Work e-mail', on.workEmail],
              // Phase 3b (default 14): what the run did in Entra, nothing more.
              ['Entra account', on.newOrTransfer === 'Transfer' ? 'Transfer: existing account kept' : on.entraCreatedAt ? `created ${instant(on.entraCreatedAt)}` : null],
              // Phase 3c (default 17): what the run did in Salesforce, from the output; the stamp when there is one.
              ['Salesforce', sfBusy ? 'Retry running…' : sfLine ? html`${sfLine}${on.salesforceProvisionedAt ? html` <span class="muted">${instant(on.salesforceProvisionedAt)}</span>` : nothing}` : null],
              ...(sfExtras ? ([['Salesforce extras', sfExtras]] as [string, unknown][]) : []),
            ])}
            <div class="facets">
              ${items('SharePoint sites', d.sharepointSites.map((x) => x.title))}
              ${items('Distribution lists', d.distroLists.map((x) => html`${x.displayName}${x.mail ? html` <span class="muted">${x.mail}</span>` : nothing}`))}
              ${items('Shared mailboxes', d.sharedMailboxes.map((x) => html`${x.label} <span class="muted">${x.mail}</span>`))}
              ${on.teamsPhone ? items('Call queues', d.callQueues.map((x) => html`${x.label} <span class="muted">${x.resourceAccountUpn}</span>`)) : nothing}
            </div>
          </section>
          <section class="card">
            <h3>Software and equipment</h3>
            <div class="facets">
              ${items('Software', d.software)}
              ${items('Equipment', [...d.equipment, ...(on.equipOther ? [`Other${on.equipOtherDescription ? `: ${on.equipOtherDescription}` : ''}`] : [])])}
            </div>
          </section>`
        : nothing}
      ${off
        ? html`<section class="card">
            <h3>Leaver</h3>
            ${dl([
              ['Name', off.employeeName],
              ['Employee account', off.employeeEmail],
              ['Person type', r.personType],
              ['Department', r.department],
              ['Manager', r.managerEmail],
            ])}
          </section>
          <section class="card">
            <h3>Access termination</h3>
            ${dl([
              ['Last day', day(off.lastDay)],
              // The stored instant, shown in Central with the zone named; the ISO UTC value next to it for the record.
              ['Access terminates', html`${instant(off.accessTerminationAt)} <span class="muted">(${off.accessTerminationAt} UTC)</span>`],
              ['Mailbox access for', off.emailAccessNeeded ? off.emailAccessRecipient : 'Not needed'],
              ['OneDrive access for', off.onedriveAccessNeeded ? off.onedriveAccessRecipient : 'Not needed'],
              // Phase 3b (default 14): stamped by the run when the account was disabled.
              ['Account terminated', off.accountTerminatedAt ? instant(off.accountTerminatedAt) : null],
              // Phase 3c (default 17): the Salesforce offboarding outcome, from the output.
              ['Salesforce', sfLine ? html`${sfLine}${off.salesforceDeactivatedAt ? html` <span class="muted">${instant(off.salesforceDeactivatedAt)}</span>` : nothing}` : null],
            ])}
          </section>
          <section class="card">
            <h3>Equipment to collect</h3>
            <div class="facets">
              ${items('Equipment', [...d.equipment, ...(off.equipOther ? [`Other${off.equipOtherDescription ? `: ${off.equipOtherDescription}` : ''}`] : [])])}
              ${devicesPanel(st.devices)}
            </div>
          </section>`
        : nothing}

      <section class="card">
        <h3>History</h3>
        <ul class="history">
          ${d.statusHistory.map(
            (h) => html`<li><span class="when">${instant(h.changedAt)}</span>
              ${h.scope}: ${h.oldStatus ? `${h.oldStatus} → ` : ''}${h.newStatus}${h.reason ? html` <span class="muted">(${h.reason})</span>` : nothing}
              <span class="muted">· ${h.changedBy}</span></li>`,
          )}
        </ul>
      </section>
      ${confirmDialog(app, d)}
      ${forceStartDialog(app)}
    </main>`;
}
